import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

const WELCOME_TOKENS = 10;
const DAILY_TOKENS = 1;

// ── 全域單例 ──────────────────────────────────────────
let globalBalance: number | null = null;
let globalHasPendingDaily = false;
let tokenListeners: Array<() => void> = [];
let tokenInitialized = false;

function notifyTokenListeners() {
  tokenListeners.forEach(fn => fn());
}

/** 台灣時間 (UTC+8) */
function getTaiwanNow(): Date {
  const utc = Date.now() + new Date().getTimezoneOffset() * 60000;
  return new Date(utc + 8 * 3600000);
}

/** 台灣今日 YYYY-MM-DD */
function getTodayTW(): string {
  const d = getTaiwanNow();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────
export function useTokens() {
  const { user, isLoggedIn } = useAuth();
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    tokenListeners.push(listener);
    return () => {
      tokenListeners = tokenListeners.filter(l => l !== listener);
    };
  }, []);

  const initTokens = useCallback(async () => {
    if (!user) return;
    try {
      const { data: existing } = await supabase
        .from('user_tokens')
        .select('balance, last_daily_grant')
        .eq('user_id', user.id)
        .maybeSingle();

      const taiwanNow = getTaiwanNow();
      const todayStr = getTodayTW();
      const currentHour = taiwanNow.getHours();

      if (!existing) {
        // 首次加入：自動送 10 張（歡迎獎勵不需手動領）
        await supabase.from('user_tokens').insert({
          user_id: user.id,
          balance: WELCOME_TOKENS,
          last_daily_grant: todayStr,
        });
        await supabase.from('token_transactions').insert({
          user_id: user.id,
          type: 'earn',
          amount: WELCOME_TOKENS,
          description: '🎉 首次加入歡迎獎勵',
        });
        globalBalance = WELCOME_TOKENS;
        globalHasPendingDaily = false;
        notifyTokenListeners();
      } else {
        globalBalance = existing.balance;
        // 每日通證：8 點後且今天尚未領取 → 標記為「待領取」，不自動發放
        globalHasPendingDaily = currentHour >= 8 && existing.last_daily_grant !== todayStr;
        notifyTokenListeners();
      }
    } catch (err) {
      console.error('Token init error:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isLoggedIn && user) {
      if (!tokenInitialized) {
        tokenInitialized = true;
        initTokens();
      }
    } else {
      globalBalance = null;
      globalHasPendingDaily = false;
      tokenInitialized = false;
      notifyTokenListeners();
    }
  }, [isLoggedIn, user?.id]);

  /** 主動領取每日通證 */
  const claimDailyToken = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: '請先登入' };
    if (!globalHasPendingDaily) return { success: false, error: '今日已領取或尚未到 8 點' };
    try {
      const todayStr = getTodayTW();
      const newBalance = (globalBalance ?? 0) + DAILY_TOKENS;
      await supabase.from('user_tokens').update({
        balance: newBalance,
        last_daily_grant: todayStr,
      }).eq('user_id', user.id);
      await supabase.from('token_transactions').insert({
        user_id: user.id,
        type: 'earn',
        amount: DAILY_TOKENS,
        description: '📅 每日登入獎勵',
      });
      globalBalance = newBalance;
      globalHasPendingDaily = false;
      notifyTokenListeners();
      return { success: true };
    } catch {
      return { success: false, error: '領取失敗，請稍後再試' };
    }
  }, [user?.id]);

  /** 扣除 1 張通證 */
  const spendToken = useCallback(async (description: string): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: '請先登入' };
    if (globalBalance === null || globalBalance < 1) {
      return { success: false, error: '通證不足，無法執行此操作' };
    }
    try {
      const newBalance = globalBalance - 1;
      const { error } = await supabase
        .from('user_tokens')
        .update({ balance: newBalance })
        .eq('user_id', user.id);
      if (error) throw error;
      await supabase.from('token_transactions').insert({
        user_id: user.id,
        type: 'spend',
        amount: 1,
        description,
      });
      globalBalance = newBalance;
      notifyTokenListeners();
      return { success: true };
    } catch {
      return { success: false, error: '操作失敗，請稍後再試' };
    }
  }, [user?.id]);

  /** 重新讀取餘額 */
  const reloadBalance = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_tokens')
      .select('balance')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) {
      globalBalance = data.balance;
      notifyTokenListeners();
    }
  }, [user?.id]);

  return {
    balance: globalBalance,
    hasPendingDailyToken: globalHasPendingDaily,
    claimDailyToken,
    spendToken,
    reloadBalance,
  };
}
